// CareerStatsTable — main column above-the-fold career statistics.
//
// Source: player_stats rows from /api/profile/:slug (PR2 extension).
// Best-season row highlight: the row with the highest points_pg gets
// a hoop-row-best background, bold numbers, and a "Best season" pill
// next to the team name. If multiple rows tie on PPG the earliest
// (newest season — array is sorted DESC) wins; this matches how a
// human would read the table.
//
// SyncBadge attribution: reused from PR1's PlayerHero. The parent passes
// `syncSource` / `syncedAt` which today are both null (no auto-synced
// stats yet). Phase A will derive these from the latest auto-synced
// row in career_stats.
//
// Empty values render "—" not 0 — per the wingspan reference, a missing
// blocks_pg should be visually distinct from a player who literally
// blocked zero shots per game.

import { type ReactNode } from 'react'
import SyncBadge from './SyncBadge'

export interface CareerStatsRow {
  season: string
  team: string | null
  league: string
  country: string | null
  games_played: number | null
  minutes_pg: number | null
  points_pg: number | null
  rebounds_pg: number | null
  assists_pg: number | null
  steals_pg: number | null
  blocks_pg: number | null
  fg_pct: number | null
  three_pct: number | null
  ft_pct: number | null
  is_3x3: boolean
  source: string
  source_url: string | null
  synced_at: string
}

interface Props {
  rows: CareerStatsRow[]
  syncSource: string | null
  syncedAt: string | null
}

function bestSeasonIndex(rows: CareerStatsRow[]): number {
  let bestIdx = -1
  let bestPpg = -Infinity
  let bestGames = -Infinity
  rows.forEach((r, i) => {
    const ppg = r.points_pg
    if (ppg == null) return
    const games = r.games_played ?? 0
    // Highest PPG wins; ties break to the larger sample (more games played).
    if (ppg > bestPpg || (ppg === bestPpg && games > bestGames)) {
      bestIdx = i
      bestPpg = ppg
      bestGames = games
    }
  })
  return bestIdx
}

function fmt(n: number | null, opts: { pct?: boolean; precision?: number } = {}): string {
  if (n == null) return '—'
  const { pct = false, precision = 1 } = opts
  if (pct) {
    // DB stores fg_pct as 0–1 (e.g. 0.452) or 0–100 — be defensive. Values
    // ≤ 1 are treated as proportions; otherwise as already-scaled percents.
    const v = n <= 1 ? n * 100 : n
    return `${v.toFixed(precision)}%`
  }
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(precision)
}

export default function CareerStatsTable({ rows, syncSource, syncedAt }: Props) {
  // UX #5 — hide the section on the read profile when there are no stats.
  if (rows.length === 0) return null
  const best = bestSeasonIndex(rows)

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-5">
      <header className="flex items-center justify-between mb-3 gap-3">
        <h2 className="text-[12px] font-bold uppercase tracking-wider text-hoop-accent">
          Career Statistics
        </h2>
        <SyncBadge source={syncSource} syncedAt={syncedAt} />
      </header>

      <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[640px] text-[12.5px]">
            <thead>
              <tr className="text-left text-hoop-text-3 border-b border-hoop-border">
                <Th>Season</Th>
                <Th>Team</Th>
                <Th align="right">G</Th>
                <Th align="right">Min</Th>
                <Th align="right">Pts</Th>
                <Th align="right">Reb</Th>
                <Th align="right">Ast</Th>
                <Th align="right">Stl</Th>
                <Th align="right">Blk</Th>
                <Th align="right">FG%</Th>
                <Th align="right">3P%</Th>
                <Th align="right">FT%</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isBest = i === best
                const baseRow = isBest
                  ? 'bg-hoop-row-best'
                  : ''
                const cellWeight = isBest
                  ? 'font-semibold text-hoop-text'
                  : 'text-hoop-text'
                return (
                  <tr
                    key={`${row.season}-${row.league}-${i}`}
                    className={`border-b border-hoop-border last:border-b-0 ${baseRow}`}
                  >
                    <Td className={cellWeight}>{row.season}</Td>
                    <Td className={cellWeight}>
                      <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <span>
                          {row.team ?? '—'}
                          {row.league && (
                            <span className="text-hoop-text-3 font-normal">
                              {' '}
                              ({row.league})
                            </span>
                          )}
                        </span>
                        {isBest && (
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-hoop-green-bg text-hoop-green border border-hoop-green/40 font-bold"
                            style={{ fontSize: '9.5px', letterSpacing: '0.04em' }}
                          >
                            Best season
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td align="right" className={cellWeight}>{fmt(row.games_played)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.minutes_pg)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.points_pg)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.rebounds_pg)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.assists_pg)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.steals_pg)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.blocks_pg)}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.fg_pct, { pct: true })}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.three_pct, { pct: true })}</Td>
                    <Td align="right" className={cellWeight}>{fmt(row.ft_pct, { pct: true })}</Td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
    </section>
  )
}

function Th({
  children,
  align = 'left',
}: {
  children: ReactNode
  align?: 'left' | 'right'
}) {
  return (
    <th
      className={`py-2 px-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
      scope="col"
    >
      {children}
    </th>
  )
}

function Td({
  children,
  className,
  align = 'left',
}: {
  children: ReactNode
  className?: string
  align?: 'left' | 'right'
}) {
  return (
    <td
      className={`py-2 px-2 ${align === 'right' ? 'text-right tabular-nums' : ''} ${className ?? ''}`}
    >
      {children}
    </td>
  )
}
